/**
 * Checkout Modal
 * Payment processing modal that supports both Stripe and Paystack
 */

import { OVERLAY_TOKENS } from '../theme';

interface CheckoutModalOptions {
  merchantId: string | number;
  sessionToken: string;
  paymentGateway?: 'stripe' | 'paystack';
  clientSecret?: string;
  publishableKey?: string;
  stripeAccountId?: string;
  accessCode?: string;
  authorizationUrl?: string;
  amount?: number;
  currency?: string;
  onSuccess: (result: {
    sessionToken: string;
    orderId: string;
    amount: number;
    currency: string;
    paymentMethod: 'stripe' | 'paystack';
  }) => void;
  onError: (error: Error) => void;
  onClose: () => void;
}

const MODAL_STYLES = `
  #khaime-checkout-modal {
    ${OVERLAY_TOKENS}
    position: fixed;
    inset: 0;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    background: rgba(17, 24, 39, 0.55);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    font-family: var(--kso-font);
    color: var(--kso-ink);
  }

  #khaime-checkout-modal * { box-sizing: border-box; }

  .ks-modal-content {
    background: var(--kso-surface);
    border-radius: var(--kso-radius-md);
    width: 100%;
    max-width: 440px;
    max-height: 90vh;
    overflow: auto;
    box-shadow: var(--kso-shadow);
    animation: ks-modal-in 260ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  @keyframes ks-modal-in {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .ks-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.125rem 1.5rem;
    border-bottom: 1px solid var(--kso-hairline);
  }

  .ks-modal-title {
    font-family: var(--kso-font-display);
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--kso-ink);
    margin: 0;
    letter-spacing: -0.01em;
  }

  .ks-modal-close {
    background: none;
    border: none;
    padding: 0.5rem;
    margin: -0.5rem;
    cursor: pointer;
    color: var(--kso-muted);
    border-radius: 999px;
    transition: color 180ms ease, background 180ms ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ks-modal-close:hover {
    color: var(--kso-ink);
    background: var(--kso-hairline);
  }

  .ks-modal-body {
    padding: 1.25rem 1.5rem 1.5rem;
  }

  .ks-modal-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;
  }

  .ks-modal-loading p {
    color: var(--kso-muted);
    font-size: 0.875rem;
    margin: 0;
  }

  .ks-modal-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--kso-hairline);
    border-top-color: var(--kso-accent);
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  @keyframes ks-spin {
    to { transform: rotate(360deg); }
  }

  .ks-modal-error {
    padding: 0.75rem 1rem;
    background: var(--kso-danger-bg);
    border: 1px solid var(--kso-danger-border);
    border-radius: var(--kso-radius-xs);
    color: var(--kso-danger-ink);
    font-size: 0.8125rem;
    text-align: center;
  }

  .ks-modal-amount {
    text-align: center;
    padding: 1.375rem 1rem;
    background: var(--kso-surface-alt);
    border: 1px solid var(--kso-hairline);
    border-radius: var(--kso-radius-sm);
    margin-bottom: 1.25rem;
  }

  .ks-modal-amount-value {
    font-family: var(--kso-font-display);
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--kso-accent);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.015em;
  }

  .ks-modal-amount-label {
    font-size: 0.8125rem;
    color: var(--kso-muted);
    margin-top: 0.375rem;
  }

  .ks-modal-secure {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    margin-top: 0.75rem;
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--kso-muted-2);
  }

  .ks-modal-secure svg {
    width: 12px;
    height: 12px;
  }

  .ks-modal-redirect-btn,
  .ks-pay-btn {
    width: 100%;
    padding: 0.9375rem 1.5rem;
    background: var(--kso-accent);
    color: var(--kso-accent-ink);
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: filter 220ms ease, transform 120ms ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .ks-modal-redirect-btn:hover:not(:disabled),
  .ks-pay-btn:hover:not(:disabled) { filter: brightness(1.08); }

  .ks-modal-redirect-btn:active:not(:disabled),
  .ks-pay-btn:active:not(:disabled) { transform: scale(0.985); }

  .ks-pay-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .ks-pay-btn svg,
  .ks-modal-redirect-btn svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .ks-stripe-element {
    padding: 1rem;
    background: var(--kso-surface-alt);
    border: 1px solid var(--kso-hairline);
    border-radius: var(--kso-radius-sm);
    margin-bottom: 1.25rem;
  }

  #stripe-error {
    font-size: 0.8125rem;
    color: var(--kso-danger);
    margin-bottom: 1rem;
  }

  .ks-pay-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  /* Result screens */
  .ks-result {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 2.25rem 1rem;
    animation: ks-result-in 420ms cubic-bezier(0.22, 0.61, 0.36, 1);
  }

  @keyframes ks-result-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ks-result-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.125rem;
  }

  .ks-result-icon svg {
    width: 28px;
    height: 28px;
  }

  .ks-result-icon.success {
    background: var(--kso-success-bg);
    color: var(--kso-success);
  }

  .ks-result-icon.failure {
    background: var(--kso-danger-bg);
    color: var(--kso-danger);
  }

  .ks-result-title {
    font-family: var(--kso-font-display);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--kso-ink);
    margin: 0 0 0.5rem;
    letter-spacing: -0.015em;
  }

  .ks-result-message {
    font-size: 0.875rem;
    color: var(--kso-muted);
    margin: 0 0 0.25rem;
    line-height: 1.5;
    max-width: 320px;
  }

  .ks-result-amount {
    font-family: var(--kso-font-display);
    font-size: 1.375rem;
    font-weight: 600;
    color: var(--kso-accent);
    margin: 0.875rem 0;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.01em;
  }

  .ks-result-id {
    font-size: 0.75rem;
    color: var(--kso-muted-2);
    font-variant-numeric: tabular-nums;
    margin: 0;
  }

  .ks-result-btn {
    margin-top: 1.25rem;
    padding: 0.8125rem 2rem;
    background: var(--kso-accent);
    color: var(--kso-accent-ink);
    border: none;
    border-radius: var(--kso-btn-radius);
    font-size: 0.9375rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: filter 220ms ease, transform 120ms ease;
  }

  .ks-result-btn:hover { filter: brightness(1.08); }
  .ks-result-btn:active { transform: scale(0.985); }

  .ks-result-btn.secondary {
    background: transparent;
    color: var(--kso-muted);
    padding: 0.5rem 1.5rem;
    margin-top: 0.5rem;
    font-size: 0.8125rem;
  }

  .ks-result-btn.secondary:hover {
    color: var(--kso-ink);
    filter: none;
  }

  @keyframes ks-check-draw {
    to { stroke-dashoffset: 0; }
  }

  .ks-result-icon.success svg path {
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    animation: ks-check-draw 0.4s ease-out 0.2s forwards;
  }

  @media (max-width: 480px) {
    #khaime-checkout-modal { padding: 0; }
    .ks-modal-content {
      max-width: 100%;
      max-height: 100%;
      height: 100%;
      border-radius: 0;
    }
  }
`;

/**
 * Format currency amount
 */
function formatAmount(amount: number, currency: string): string {
  const curr = currency.toUpperCase();
  const symbols: Record<string, string> = {
    USD: '$', EUR: '€', GBP: '£', NGN: '₦', GHS: 'GH₵',
    KES: 'KSh', ZAR: 'R', RWF: 'RF', TZS: 'TSh', UGX: 'USh', XOF: 'CFA',
  };
  const symbol = symbols[curr] || curr + ' ';
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Open checkout modal with payment form
 */
export async function openCheckoutModal(options: CheckoutModalOptions): Promise<void> {
  const {
    sessionToken,
    paymentGateway = 'stripe',
    clientSecret,
    publishableKey,
    stripeAccountId,
    accessCode,
    authorizationUrl,
    amount = 0,
    currency = 'USD',
    onSuccess,
    onError,
    onClose,
  } = options;

  // Create modal container
  const modal = document.createElement('div');
  modal.id = 'khaime-checkout-modal';
  modal.innerHTML = `
    <style>${MODAL_STYLES}</style>
    <div class="ks-modal-content">
      <header class="ks-modal-header">
        <h3 class="ks-modal-title">Complete Payment</h3>
        <button class="ks-modal-close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>
      <div class="ks-modal-body">
        <div class="ks-modal-loading">
          <div class="ks-modal-spinner"></div>
          <p>Loading payment form...</p>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Close button handler
  modal.querySelector('.ks-modal-close')?.addEventListener('click', () => {
    closeModal();
    onClose();
  });

  // Backdrop click handler
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
      onClose();
    }
  });

  // Escape key handler
  const escapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal();
      onClose();
    }
  };
  document.addEventListener('keydown', escapeHandler);

  function closeModal() {
    document.removeEventListener('keydown', escapeHandler);
    modal.remove();
  }

  const body = modal.querySelector('.ks-modal-body')!;
  const titleEl = modal.querySelector('.ks-modal-title')!;

  function showSuccessScreen(orderId: string, successResult: Parameters<typeof onSuccess>[0]) {
    titleEl.textContent = 'Payment Complete';
    body.innerHTML = `
      <div class="ks-result">
        <div class="ks-result-icon success">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
          </svg>
        </div>
        <h4 class="ks-result-title">Payment Successful</h4>
        <p class="ks-result-message">Your payment has been processed successfully.</p>
        <div class="ks-result-amount">${formatAmount(successResult.amount, successResult.currency)}</div>
        <p class="ks-result-id">Order ID: ${orderId.length > 24 ? orderId.slice(0, 24) + '...' : orderId}</p>
        <button class="ks-result-btn" id="ks-result-done">Done</button>
      </div>
    `;

    modal.querySelector('#ks-result-done')?.addEventListener('click', () => {
      onSuccess(successResult);
      closeModal();
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      if (document.getElementById('khaime-checkout-modal')) {
        onSuccess(successResult);
        closeModal();
      }
    }, 5000);
  }

  function showFailureScreen(errorMessage: string, onRetry?: () => void) {
    titleEl.textContent = 'Payment Failed';
    body.innerHTML = `
      <div class="ks-result">
        <div class="ks-result-icon failure">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </div>
        <h4 class="ks-result-title">Payment Failed</h4>
        <p class="ks-result-message">${errorMessage}</p>
        ${onRetry ? '<button class="ks-result-btn" id="ks-result-retry">Try Again</button>' : ''}
        <button class="ks-result-btn secondary" id="ks-result-close">Close</button>
      </div>
    `;

    if (onRetry) {
      modal.querySelector('#ks-result-retry')?.addEventListener('click', onRetry);
    }
    modal.querySelector('#ks-result-close')?.addEventListener('click', () => {
      closeModal();
      onClose();
    });
  }

  // Handle different payment gateways
  if (paymentGateway === 'paystack' && (authorizationUrl || accessCode)) {
    // Paystack: Redirect to payment page
    body.innerHTML = `
      <div class="ks-modal-amount">
        <div class="ks-modal-amount-value">${formatAmount(amount, currency)}</div>
        <div class="ks-modal-amount-label">Total amount</div>
        <div class="ks-modal-secure">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Secure payment
        </div>
      </div>
      <button class="ks-modal-redirect-btn" id="paystack-redirect">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        Pay with Paystack
      </button>
    `;

    const redirectBtn = body.querySelector('#paystack-redirect');
    redirectBtn?.addEventListener('click', () => {
      const paymentUrl = authorizationUrl || `https://checkout.paystack.com/${accessCode}`;

      // Open Paystack in popup
      const popup = window.open(paymentUrl, 'paystack_payment', 'width=500,height=600');

      if (popup) {
        // Success requires an explicit postMessage from /sdk/checkout-return
        // (the popup's own page). Bare `popup.closed` is NOT a success signal
        // — it also fires when the user cancels, when popups are silently
        // blocked (window.open returns a reference that's immediately
        // closed), or when Paystack fails to load. The webhook remains the
        // server-side source of truth; this flag just gates the UX.
        let paymentReturned = false;
        let paystackReference: string | undefined;

        const onMessage = (ev: MessageEvent) => {
          const data = ev.data;
          if (!data || data.type !== 'khaime-checkout-return') return;
          paymentReturned = true;
          paystackReference = data.reference || undefined;
        };
        window.addEventListener('message', onMessage);

        const pollTimer = setInterval(() => {
          if (!popup.closed) return;
          clearInterval(pollTimer);
          window.removeEventListener('message', onMessage);

          if (paymentReturned) {
            const orderId = paystackReference || `ORD-${Date.now()}`;
            showSuccessScreen(orderId, {
              sessionToken,
              orderId,
              amount,
              currency,
              paymentMethod: 'paystack',
            });
          } else {
            // Popup closed without a return message — treat as cancelled.
            // The SDK's higher-level onClose handler tears down the modal.
            onClose();
          }
        }, 500);
      } else {
        // Popup blocked, redirect instead
        window.location.href = authorizationUrl || `https://checkout.paystack.com/${accessCode}`;
      }
    });

  } else if (paymentGateway === 'stripe' && clientSecret) {
    // Stripe: Use Stripe Elements
    const lockIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
    body.innerHTML = `
      <div class="ks-modal-amount">
        <div class="ks-modal-amount-value">${formatAmount(amount, currency)}</div>
        <div class="ks-modal-amount-label">Total amount</div>
        <div class="ks-modal-secure">
          ${lockIcon}
          Secure payment
        </div>
      </div>
      <div id="stripe-element" class="ks-stripe-element"></div>
      <div id="stripe-error" style="color: #dc2626; display: none;"></div>
      <button class="ks-pay-btn" id="stripe-pay-btn" disabled>
        ${lockIcon}
        Pay ${formatAmount(amount, currency)}
      </button>
    `;

    // Load Stripe
    try {
      console.log('[Khaime Checkout] Stripe init:', {
        publishableKey: publishableKey?.substring(0, 12) + '...',
        stripeAccountId,
        clientSecretPrefix: clientSecret?.substring(0, 20) + '...',
      });
      const stripe = await loadStripe(publishableKey!, stripeAccountId);
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const elements = stripe.elements({ clientSecret });
      const paymentElement = elements.create('payment');
      paymentElement.mount('#stripe-element');

      const payBtn = body.querySelector('#stripe-pay-btn') as HTMLButtonElement;
      const errorDiv = body.querySelector('#stripe-error') as HTMLDivElement;

      paymentElement.on('ready', () => {
        payBtn.disabled = false;
      });

      paymentElement.on('change', (event: any) => {
        if (event.error) {
          errorDiv.textContent = event.error.message;
          errorDiv.style.display = 'block';
        } else {
          errorDiv.style.display = 'none';
        }
      });

      payBtn.addEventListener('click', async () => {
        payBtn.disabled = true;
        payBtn.innerHTML = '<span class="ks-pay-spinner"></span> Processing...';

        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: window.location.href,
          },
          redirect: 'if_required',
        });

        if (error) {
          showFailureScreen(error.message || 'Payment failed. Please try again.', () => {
            // Retry: re-render the payment form
            titleEl.textContent = 'Complete Payment';
            body.innerHTML = `
              <div class="ks-modal-amount">
                <div class="ks-modal-amount-value">${formatAmount(amount, currency)}</div>
                <div class="ks-modal-amount-label">Total amount</div>
                <div class="ks-modal-secure">
                  ${lockIcon}
                  Secure payment
                </div>
              </div>
              <div id="stripe-element" class="ks-stripe-element"></div>
              <div id="stripe-error" style="color: #dc2626; display: none;"></div>
              <button class="ks-pay-btn" id="stripe-pay-btn" disabled>
                ${lockIcon}
                Pay ${formatAmount(amount, currency)}
              </button>
            `;
            // Re-mount Stripe elements
            const newPaymentElement = elements.create('payment');
            newPaymentElement.mount('#stripe-element');
            const newPayBtn = body.querySelector('#stripe-pay-btn') as HTMLButtonElement;
            newPaymentElement.on('ready', () => { newPayBtn.disabled = false; });
            newPayBtn.addEventListener('click', async () => {
              newPayBtn.disabled = true;
              newPayBtn.innerHTML = '<span class="ks-pay-spinner"></span> Processing...';
              const retryResult = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
              });
              if (retryResult.error) {
                showFailureScreen(retryResult.error.message || 'Payment failed. Please try again.');
              } else if (retryResult.paymentIntent?.status === 'succeeded') {
                showSuccessScreen(retryResult.paymentIntent.id, {
                  sessionToken, orderId: retryResult.paymentIntent.id, amount, currency, paymentMethod: 'stripe',
                });
              }
            });
          });
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          showSuccessScreen(paymentIntent.id, {
            sessionToken,
            orderId: paymentIntent.id,
            amount,
            currency,
            paymentMethod: 'stripe',
          });
        } else {
          // Handle other statuses
          errorDiv.textContent = 'Payment requires additional action';
          errorDiv.style.display = 'block';
          payBtn.disabled = false;
          payBtn.innerHTML = `${lockIcon} Pay ${formatAmount(amount, currency)}`;
        }
      });

    } catch (error) {
        console.log('Error loading Stripe:', error);
      body.innerHTML = `
        <div class="ks-modal-error">
          <p>Failed to load payment form. Please try again.</p>
        </div>
      `;
      onError(error instanceof Error ? error : new Error('Failed to load Stripe'));
    }

  } else {
    // Fallback: Show processing message (for legacy flow)
    body.innerHTML = `
      <div style="text-align: center; padding: 2rem;">
        <p style="margin-bottom: 1rem; color: #6b7280;">
          Processing your payment...
        </p>
        <p style="font-size: 0.875rem; color: #9ca3af;">
          Session: ${sessionToken.slice(0, 16)}...
        </p>
      </div>
    `;

    // Simulate success for development
    setTimeout(() => {
      const orderId = `ORD-${Date.now()}`;
      showSuccessScreen(orderId, {
        sessionToken,
        orderId,
        amount,
        currency,
        paymentMethod: 'stripe',
      });
    }, 2000);
  }
}

/**
 * Load Stripe.js dynamically
 */
async function loadStripe(publishableKey: string, stripeAccountId?: string): Promise<any> {
  const stripeOptions = stripeAccountId ? { stripeAccount: stripeAccountId } : undefined;

  // Check if already loaded
  if ((window as any).Stripe) {
    return (window as any).Stripe(publishableKey, stripeOptions);
  }

  // Load Stripe.js
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve((window as any).Stripe(publishableKey, stripeOptions));
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default openCheckoutModal;
